import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { allProjects } from '../../../mocks/projects';
import ProjectsNavbar from '../components/ProjectsNavbar';
import Footer from '../../home/components/Footer';

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  
  const project = allProjects.find(p => p.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Proyecto no encontrado</h1>
          <Link to="/proyectos" className="text-orange-500 hover:text-orange-600">
            Volver a proyectos
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <ProjectsNavbar />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
        <img
          src={project.gallery ? project.gallery[0] : project.image}
          alt={project.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <button
              onClick={() => navigate('/proyectos')}
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span className="text-sm">Volver a Proyectos</span>
            </button>
            
            <div className="inline-block px-4 py-2 bg-orange-500/90 text-white text-xs font-semibold rounded-full mb-4">
              {project.industry}
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-4">
              {project.title}
            </h1>
            
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mb-8">
              {project.description}
            </p>
            
            <div className="flex flex-wrap gap-6">
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-2xl">
                <div className="text-white/70 text-xs mb-1">Cliente</div>
                <div className="text-white font-bold text-sm md:text-base">{project.client}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-2xl">
                <div className="text-white/70 text-xs mb-1">Año</div>
                <div className="text-white font-bold text-sm md:text-base">{project.year}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-2xl">
                <div className="text-white/70 text-xs mb-1">Eficiencia</div>
                <div className="text-green-400 font-bold text-sm md:text-base">{project.stats.efficiency}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-2xl">
                <div className="text-white/70 text-xs mb-1">Desarrollo</div>
                <div className="text-white font-bold text-sm md:text-base">{project.stats.time}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-6 py-3 rounded-2xl">
                <div className="text-white/70 text-xs mb-1">ROI</div>
                <div className="text-orange-400 font-bold text-sm md:text-base">{project.stats.roi}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Full Description */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Descripción del Proyecto
                </h2>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  {project.fullDescription}
                </p>
              </div>

              {/* Challenge */}
              <div className="bg-orange-50 rounded-3xl p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-2xl flex-shrink-0">
                    <i className="ri-error-warning-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                      El Desafío
                    </h3>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      {project.challenge}
                    </p>
                  </div>
                </div>
              </div>

              {/* Solution */}
              <div className="bg-green-50 rounded-3xl p-6 md:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-2xl flex-shrink-0">
                    <i className="ri-lightbulb-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                      Nuestra Solución
                    </h3>
                    <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                      {project.solution}
                    </p>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                  Resultados Obtenidos
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {project.results?.map((result, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-3 bg-gray-50 rounded-2xl p-4"
                    >
                      <div className="w-6 h-6 flex items-center justify-center bg-gradient-to-br from-orange-500 to-green-500 rounded-full flex-shrink-0 mt-1">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <p className="text-sm md:text-base text-gray-700">{result}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Gallery */}
              {project.gallery && project.gallery.length > 0 && (
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                    Galería del Proyecto
                  </h3>
                  
                  {/* Main Image */}
                  <div className="mb-4 rounded-3xl overflow-hidden">
                    <div className="w-full h-[400px] md:h-[500px]">
                      <img
                        src={project.gallery[selectedImage]}
                        alt={`${project.title} - Imagen ${selectedImage + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  
                  {/* Thumbnails */}
                  <div className="grid grid-cols-4 gap-4">
                    {project.gallery.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={`rounded-2xl overflow-hidden transition-all cursor-pointer ${
                          selectedImage === index
                            ? 'ring-4 ring-orange-500 scale-105'
                            : 'hover:scale-105 opacity-70 hover:opacity-100'
                        }`}
                      >
                        <div className="w-full h-24 md:h-32">
                          <img
                            src={img}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-8">
              {/* Technologies */}
              <div className="bg-gray-50 rounded-3xl p-6 md:p-8 sticky top-24">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
                  Tecnologías Utilizadas
                </h3>
                <div className="space-y-3">
                  {project.technologies?.map((tech, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 bg-white rounded-xl p-3"
                    >
                      <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-orange-500 to-green-500 rounded-lg flex-shrink-0">
                        <i className="ri-cpu-line text-white text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{tech}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    ¿Proyecto Similar?
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Podemos desarrollar una solución personalizada para tu empresa.
                  </p>
                  <Link
                    to="/#contact"
                    className="block w-full text-center px-6 py-3 bg-gradient-to-r from-orange-500 to-green-500 text-white text-sm font-semibold rounded-full hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Solicitar Consultoría
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12">
            Proyectos Relacionados
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {allProjects
              .filter(p => p.industry === project.industry && p.id !== project.id)
              .slice(0, 3)
              .map((relatedProject) => (
                <Link
                  key={relatedProject.id}
                  to={`/proyectos/${relatedProject.id}`}
                  className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer hover:-translate-y-2"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={relatedProject.image}
                      alt={relatedProject.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  
                  <div className="p-6">
                    <div className="text-orange-500 text-xs font-semibold mb-2">
                      {relatedProject.industry}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {relatedProject.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {relatedProject.description}
                    </p>
                    <div className="flex items-center gap-2 text-orange-500 text-sm font-semibold">
                      Ver Proyecto
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
